// src/pages/UploadProductSkuMaster.jsx
import React, { useState, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "../utils/apiUrls";

const SAMPLE_FILE_URL = "/sku_product_master_sample_upload.csv";

const CSV_COLUMNS = [
  "productSkuId",
  "productName",
  "productHsnCode",
  "productWeightUnit",
  "productWeightValue",
  "productDimensionUnit",
  "productLength",
  "productWidth",
  "productHeight",
  "unitPrice",
  "currency",
];

const FIXED_WEIGHT_UNIT = "g";
const FIXED_DIMENSION_UNIT = "cm";
const FIXED_CURRENCY = "INR";

const emptyRow = () => ({
  productSkuId: "",
  productName: "",
  productHsnCode: "",
  productWeightUnit: FIXED_WEIGHT_UNIT,
  productWeightValue: "",
  productDimensionUnit: FIXED_DIMENSION_UNIT,
  productLength: "",
  productWidth: "",
  productHeight: "",
  unitPrice: "",
  currency: FIXED_CURRENCY,
});

const UploadProductSkuMaster = () => {
  const [mode, setMode] = useState("upload"); // "upload" | "manual"
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { success: boolean, message: string }
  const [rowErrors, setRowErrors] = useState({}); // { rowIndex: { fieldName: errorMsg } }
  const fileInputRef = useRef(null);

  // ---------- File upload handlers ----------
  const handleFileSelect = (selectedFile) => {
    setResult(null);
    if (!selectedFile) return;
    const isCSV =
      selectedFile.type === "text/csv" ||
      selectedFile.name.toLowerCase().endsWith(".csv");
    if (!isCSV) {
      setResult({ success: false, message: "Only CSV files are allowed." });
      return;
    }
    setFile(selectedFile);
  };

  const handleFileInputChange = (e) => {
    handleFileSelect(e.target.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files?.[0]);
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ---------- Manual rows handlers ----------
  const updateRow = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
    setRowErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const next = { ...prev };
      next[index] = { ...next[index] };
      delete next[index][field];
      if (Object.keys(next[index]).length === 0) delete next[index];
      return next;
    });
  };

  const addRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const duplicateRow = (index) => {
    setRows((prev) => {
      const copy = [...prev];
      copy.splice(index + 1, 0, { ...prev[index] });
      return copy;
    });
  };

  const removeRow = (index) => {
    setRows((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== index)));
    setRowErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const resetManualForm = () => {
    setRows([emptyRow()]);
    setRowErrors({});
    setResult(null);
  };

  // ---------- Validation ----------
  const validateRows = () => {
    const errors = {};
    const isPositiveNumber = (v) => v !== "" && !isNaN(Number(v)) && Number(v) > 0;

    rows.forEach((row, i) => {
      const rowErr = {};
      if (!row.productSkuId.trim()) rowErr.productSkuId = "Required";
      if (!row.productName.trim()) rowErr.productName = "Required";
      if (!row.productHsnCode.trim()) rowErr.productHsnCode = "Required";
      if (!isPositiveNumber(row.productWeightValue)) rowErr.productWeightValue = "Must be > 0";
      if (!isPositiveNumber(row.productLength)) rowErr.productLength = "Must be > 0";
      if (!isPositiveNumber(row.productWidth)) rowErr.productWidth = "Must be > 0";
      if (!isPositiveNumber(row.productHeight)) rowErr.productHeight = "Must be > 0";
      if (!isPositiveNumber(row.unitPrice)) rowErr.unitPrice = "Must be > 0";
      if (Object.keys(rowErr).length > 0) errors[i] = rowErr;
    });
    return errors;
  };

  // ---------- Build CSV from manual rows ----------
  const escapeCSVValue = (val) => {
    if (val == null) return "";
    const str = String(val);
    if (str.includes(",") || str.includes('"') || str.includes("\n")) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const buildCsvFromRows = () => {
    const header = CSV_COLUMNS.join(",");
    const dataRows = rows.map((r) => CSV_COLUMNS.map((col) => escapeCSVValue(r[col])).join(","));
    return [header, ...dataRows].join("\n");
  };

  // ---------- Submit ----------
  const handleSubmit = async () => {
    setResult(null);

    const merchantId = localStorage.getItem("merchantId");
    const authToken = localStorage.getItem("authToken");
    if (!merchantId || !authToken) {
      setResult({ success: false, message: "Authentication missing. Please log in again." });
      return;
    }

    let uploadFile = null;

    if (mode === "upload") {
      if (!file) {
        setResult({ success: false, message: "Please select a CSV file to upload." });
        return;
      }
      uploadFile = file;
    } else {
      const errors = validateRows();
      if (Object.keys(errors).length > 0) {
        setRowErrors(errors);
        setResult({ success: false, message: "Please fix the highlighted fields before submitting." });
        return;
      }
      const csvContent = buildCsvFromRows();
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const fileName = `manual_sku_master_${Date.now()}.csv`;
      uploadFile = new File([blob], fileName, { type: "text/csv" });
    }

    const formData = new FormData();
    formData.append("productMaster", uploadFile);

    setLoading(true);
    try {
      const response = await axios.post(API.uploadProductSkuMaster(merchantId), formData, {
        headers: {
          Authorization: authToken,
        },
      });
      const successMsg =
        response.data?.message ||
        `Successfully uploaded ${mode === "manual" ? `${rows.length} record(s)` : "your file"}.`;
      setResult({ success: true, message: successMsg });
      if (mode === "upload") removeFile();
      else resetManualForm();
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to upload product SKU master. Please try again.";
      setResult({ success: false, message: errMsg });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">
          Upload Product SKU Master
        </h2>
        <p className="text-sm text-gray-600">
          Upload a CSV file or add records manually. Each SKU should include weight, dimensions and pricing details.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="bg-gray-100 p-1 rounded-xl inline-flex mb-6 w-full sm:w-auto">
        <button
          onClick={() => { setMode("upload"); setResult(null); }}
          className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
            mode === "upload" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
          }`}
        >
          📁 Upload CSV
        </button>
        <button
          onClick={() => { setMode("manual"); setResult(null); }}
          className={`flex-1 sm:flex-none px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-all ${
            mode === "manual" ? "bg-white text-blue-600 shadow-sm" : "text-gray-600 hover:text-gray-900"
          }`}
        >
          ✏️ Enter Manually
        </button>
      </div>

      {/* Sample file CTA - always visible */}
      <div className="mb-6 flex flex-wrap items-center gap-2 justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2 text-sm text-blue-900">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Need help with the format? Download the sample CSV template.</span>
        </div>
        <a
          href={SAMPLE_FILE_URL}
          download
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Download Sample
        </a>
      </div>

      {/* ============== UPLOAD MODE ============== */}
      <AnimatePresence mode="wait">
        {mode === "upload" && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !file && fileInputRef.current?.click()}
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all ${
                isDragging
                  ? "border-blue-500 bg-blue-50"
                  : file
                  ? "border-green-400 bg-green-50"
                  : "border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer"
              }`}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileInputChange}
                className="hidden"
              />

              {file ? (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-3">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-gray-900">{file.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(file.size / 1024).toFixed(2)} KB</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); removeFile(); }}
                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 bg-white border border-red-200 text-red-600 text-xs font-medium rounded-lg hover:bg-red-50 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mb-3">
                    <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className="text-base font-semibold text-gray-900">
                    {isDragging ? "Drop your CSV file here" : "Click to browse or drag & drop"}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Only .csv files are accepted</p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* ============== MANUAL MODE ============== */}
        {mode === "manual" && (
          <motion.div
            key="manual"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="space-y-4">
              {rows.map((row, idx) => (
                <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gradient-to-br from-gray-50 to-white relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">
                        {idx + 1}
                      </span>
                      <h4 className="font-semibold text-sm text-gray-700">SKU Record</h4>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => duplicateRow(idx)}
                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                        title="Duplicate this row"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                      {rows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(idx)}
                          className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Remove this row"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <Field label="SKU ID" required error={rowErrors[idx]?.productSkuId}>
                      <input
                        type="text"
                        value={row.productSkuId}
                        onChange={(e) => updateRow(idx, "productSkuId", e.target.value)}
                        placeholder="e.g. SKU-001"
                        className={inputClass(rowErrors[idx]?.productSkuId)}
                      />
                    </Field>
                    <Field label="Product Name" required error={rowErrors[idx]?.productName}>
                      <input
                        type="text"
                        value={row.productName}
                        onChange={(e) => updateRow(idx, "productName", e.target.value)}
                        placeholder="e.g. Wireless Mouse"
                        className={inputClass(rowErrors[idx]?.productName)}
                      />
                    </Field>
                    <Field label="HSN Code" required error={rowErrors[idx]?.productHsnCode}>
                      <input
                        type="text"
                        value={row.productHsnCode}
                        onChange={(e) => updateRow(idx, "productHsnCode", e.target.value)}
                        placeholder="e.g. 84716060"
                        className={inputClass(rowErrors[idx]?.productHsnCode)}
                      />
                    </Field>

                    <Field label="Weight (g)" required error={rowErrors[idx]?.productWeightValue}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.productWeightValue}
                        onChange={(e) => updateRow(idx, "productWeightValue", e.target.value)}
                        placeholder="150"
                        className={inputClass(rowErrors[idx]?.productWeightValue)}
                      />
                    </Field>

                    <Field label="Length (cm)" required error={rowErrors[idx]?.productLength}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.productLength}
                        onChange={(e) => updateRow(idx, "productLength", e.target.value)}
                        placeholder="12.0"
                        className={inputClass(rowErrors[idx]?.productLength)}
                      />
                    </Field>
                    <Field label="Width (cm)" required error={rowErrors[idx]?.productWidth}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.productWidth}
                        onChange={(e) => updateRow(idx, "productWidth", e.target.value)}
                        placeholder="6.5"
                        className={inputClass(rowErrors[idx]?.productWidth)}
                      />
                    </Field>
                    <Field label="Height (cm)" required error={rowErrors[idx]?.productHeight}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.productHeight}
                        onChange={(e) => updateRow(idx, "productHeight", e.target.value)}
                        placeholder="3.5"
                        className={inputClass(rowErrors[idx]?.productHeight)}
                      />
                    </Field>

                    <Field label="Unit Price (INR)" required error={rowErrors[idx]?.unitPrice}>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={row.unitPrice}
                        onChange={(e) => updateRow(idx, "unitPrice", e.target.value)}
                        placeholder="799.00"
                        className={inputClass(rowErrors[idx]?.unitPrice)}
                      />
                    </Field>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addRow}
                className="w-full py-3 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 font-semibold text-sm hover:bg-blue-50 hover:border-blue-400 transition-all flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another SKU
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Result message */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`mt-6 p-4 rounded-lg border-2 flex items-start gap-3 ${
            result.success
              ? "bg-green-50 border-green-300 text-green-900"
              : "bg-red-50 border-red-300 text-red-900"
          }`}
        >
          {result.success ? (
            <svg className="w-6 h-6 flex-shrink-0 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-6 h-6 flex-shrink-0 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          )}
          <div className="flex-grow">
            <p className="font-semibold mb-0.5">{result.success ? "Success" : "Error"}</p>
            <p className="text-sm">{result.message}</p>
          </div>
        </motion.div>
      )}

      {/* Submit button */}
      <div className="mt-6 flex flex-wrap gap-3 justify-end">
        {mode === "manual" && (
          <button
            type="button"
            onClick={resetManualForm}
            disabled={loading}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium text-sm transition-colors disabled:opacity-50"
          >
            Reset
          </button>
        )}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={loading || (mode === "upload" && !file)}
          className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold text-sm hover:from-blue-700 hover:to-indigo-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 shadow-md"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Uploading...
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              {mode === "upload" ? "Upload File" : `Submit ${rows.length} Record${rows.length > 1 ? "s" : ""}`}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ----- Reusable subcomponents (kept in same file for simplicity) -----
const inputClass = (error) =>
  `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-colors ${
    error
      ? "border-red-400 focus:ring-red-300 bg-red-50"
      : "border-gray-300 focus:ring-blue-400 focus:border-blue-400 bg-white"
  }`;

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">
      {label}
      {required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

export default UploadProductSkuMaster;
